/**
 * The sections the Kora Behavior page renders — the source of truth for
 * rendering and validation. The parser (wire → model) lives separately
 * in `@/lib/utils/clinic-configuration/kora/kora-parsers.ts`; this file holds only data
 * description, no runtime logic.
 *
 * Page schema, not resource schema: these sections and the ones in
 * `follow-ups.schema.ts` are stored on the same `Basic`, and
 * `clinicConfigurationSchema` is the whole of it. Anything resolving a
 * section against what is stored belongs there, not here.
 *
 * `required` mirrors `min` on the matching slice in the backend's
 * `fhir-toolkit/src/fhir_toolkit/fhir_extensions.py` StructureDefinitions, so
 * the two stay one contract. The `Basic` carries no `meta.profile`, which
 * means nothing on the server enforces it — a field marked optional here is
 * simply accepted, and the drift only shows up as data a consumer of the
 * profile considers invalid.
 */
import Sparkles from "lucide-react/dist/esm/icons/sparkles";

import type { ConfigSchema } from "@/types/clinic-configuration";
import { KORA_OVERVIEW_ANCHOR_ID } from "@/constants/clinic-configuration.constants";
import {
  FHIR_EXTENSION_URLS,
  FHIR_EXTENSIONS,
} from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";
import {
  preferredEmojisLabels,
  toneFormalityDescriptions,
  toneFormalityLabels,
  tonePunctuationDescriptions,
  tonePunctuationLabels,
} from "@/content/clinic-configuration";

/**
 * Needs for i18next-scanner
 */
// t('Kora Behavior')
// t('Identity')
// t('Who Kora is for this clinic')
// t('Agent name')
// t('Display name of the agent')
// t('Role description')
// t("Narrative description of the agent's role")
// t('Code name')
// t('Machine-readable code name for the agent')
// t('Greeting')
// t('Optional greeting line used to open a conversation')
// t('Ask name in greeting')
// t("Whether the agent asks the patient's name in the greeting")
// t('Kora asks for the name when greeting')
// t("Kora doesn't ask for the name")
// t('Tone')
// t("How Kora sounds. Applies to all of this clinic's messages")
// t('Formality of address')
// t('Formality level for patient address')
// t('Punctuation style')
// t('Punctuation style for agent messages')
// t('Emojis allowed')
// t('Whether emojis are allowed')
// t('Preferred emojis')
// t('Which emoji set the agent should use')
// t('Preferred emojis description')
// t('Narrative description of the preferred emoji setting')
// t('Escalation')
// t('When Kora hands a conversation to a human coordinator without the patient asking')
// t('Auto-escalation rate')
// t('0% = Kora never escalates on its own · 100% = every conversation goes to a coordinator')
// t('Kora never escalates on its own: it handles every conversation.')
// t('About {{value}} out of every 100 conversations go to a human coordinator.')
// t('Every conversation goes to a human coordinator.')
// t('Session labels')
// t('What coordinators see when an escalated conversation reaches them')
// t('Add a label and press Enter')
// t('Private note')
// t('Private note added to the conversation on escalation')
// t('Recommended services')
// t('Services Kora recommends to new and existing patients')
// t('New patients')
// t('Recommended services narrative for new patients')
// t('Existing patients')
// t('Recommended services narrative for existing patients')
// t('Scheduling')
// t('How Kora offers appointment slots')
// t('Max slots per message')
// t('Maximum number of slots rendered per agent message')
// t('appointment options')
// t('Scheduling buffer')
// t('How far ahead of now the earliest offered slot must be')
// t('Cancellation buffer')
// t('Minimum notice required to cancel an existing appointment')
// t('Rescheduling buffer')
// t('Minimum notice required to reschedule an existing appointment')
export const koraConfigSchema = {
  extensions: {
    identity: {
      kind: "object",
      slug: FHIR_EXTENSIONS.KORA_CONFIG_IDENTITY,
      title: "Identity",
      description: "Who Kora is for this clinic",
      fields: {
        agentName: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_IDENTITY.AGENT_NAME,
          title: "Agent name",
          description: "Display name of the agent",
          required: true,
          orientation: "horizontal",
        },
        roleDescription: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_IDENTITY.ROLE_DESCRIPTION,
          title: "Role description",
          description: "Narrative description of the agent's role",
          required: true,
          orientation: "horizontal",
        },
        codeName: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_IDENTITY.CODE_NAME,
          title: "Code name",
          description: "Machine-readable code name for the agent",
          required: true,
          orientation: "horizontal",
        },
        greeting: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_IDENTITY.GREETING,
          title: "Greeting",
          description: "Optional greeting line used to open a conversation",
          orientation: "horizontal",
        },
        askNameInGreeting: {
          kind: "boolean",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_IDENTITY.ASK_NAME_IN_GREETING,
          title: "Ask name in greeting",
          description:
            "Whether the agent asks the patient's name in the greeting",
          orientation: "horizontal",
          presentation: {
            trueLabel: "Kora asks for the name when greeting",
            falseLabel: "Kora doesn't ask for the name",
          },
        },
      },
    },
    tone: {
      kind: "object",
      slug: FHIR_EXTENSIONS.KORA_CONFIG_TONE,
      title: "Tone",
      description: "How Kora sounds. Applies to all of this clinic's messages",
      fields: {
        formalityAddress: {
          kind: "code",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_TONE.FORMALITY_ADDRESS,
          title: "Formality of address",
          description: "Formality level for patient address",
          required: true,
          labels: toneFormalityLabels,
          valueType: "code",
          /**
           * A few options a clinic picks once and lives with — worth showing
           * side by side rather than hiding one keystroke behind a trigger,
           * since each carries a sample line and the choice is made by
           * comparing them.
           */
          presentation: {
            variant: "radio",
            orientation: "horizontal",
            descriptions: toneFormalityDescriptions,
          },
        },
        punctuationStyle: {
          kind: "code",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_TONE.PUNCTUATION_STYLE,
          title: "Punctuation style",
          description: "Punctuation style for agent messages",
          required: true,
          labels: tonePunctuationLabels,
          valueType: "code",
          presentation: {
            variant: "radio",
            orientation: "horizontal",
            descriptions: tonePunctuationDescriptions,
          },
        },
        emojisAllowed: {
          kind: "boolean",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_TONE.EMOJIS_ALLOWED,
          title: "Emojis allowed",
          description: "Whether emojis are allowed",
          required: true,
          appearance: "standard",
          orientation: "horizontal",
        },
        preferredEmojis: {
          kind: "code",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_TONE.PREFERRED_EMOJIS,
          title: "Preferred emojis",
          description: "Which emoji set the agent should use",
          required: true,
          labels: preferredEmojisLabels,
          valueType: "code",
        },
        preferredEmojisDescription: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_TONE
            .PREFERRED_EMOJIS_DESCRIPTION,
          title: "Preferred emojis description",
          description: "Narrative description of the preferred emoji setting",
          required: true,
        },
      },
    },
    escalation: {
      kind: "object",
      slug: FHIR_EXTENSIONS.KORA_CONFIG_ESCALATION,
      title: "Escalation",
      description:
        "When Kora hands a conversation to a human coordinator without the patient asking",
      fields: {
        autoRate: {
          kind: "percent",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION.AUTO_RATE,
          title: "Auto-escalation rate",
          description:
            "0% = Kora never escalates on its own · 100% = every conversation goes to a coordinator",
          required: true,
          appearance: "standard",
          presentation: {
            step: 5,
            zeroDescription:
              "Kora never escalates on its own: it handles every conversation.",
            midDescription:
              "About {{value}} out of every 100 conversations go to a human coordinator.",
            fullDescription: "Every conversation goes to a human coordinator.",
          },
        },
        sessionLabels: {
          kind: "list",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION.SESSION_LABEL,
          title: "Session labels",
          description:
            "What coordinators see when an escalated conversation reaches them",
          placeholder: "Add a label and press Enter",
        },
        privateNote: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_ESCALATION.PRIVATE_NOTE,
          title: "Private note",
          description: "Private note added to the conversation on escalation",
          layout: "stacked",
        },
      },
    },
    recommendedServices: {
      kind: "object",
      slug: FHIR_EXTENSIONS.KORA_CONFIG_RECOMMENDED_SERVICES,
      title: "Recommended services",
      description: "Services Kora recommends to new and existing patients",
      fields: {
        newPatient: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_RECOMMENDED_SERVICES
            .NEW_PATIENT,
          title: "New patients",
          description: "Recommended services narrative for new patients",
          layout: "stacked",
        },
        existingPatient: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_RECOMMENDED_SERVICES
            .EXISTING_PATIENT,
          title: "Existing patients",
          description: "Recommended services narrative for existing patients",
          layout: "stacked",
        },
      },
    },
    scheduling: {
      kind: "object",
      slug: FHIR_EXTENSIONS.KORA_CONFIG_SCHEDULING,
      title: "Scheduling",
      description: "How Kora offers appointment slots",
      fields: {
        /**
         * Every field here is a single-line control over a short number, so
         * the section reads as a settings list rather than four tall stacks.
         */
        maxSlotsPerMessage: {
          kind: "number",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_SCHEDULING
            .MAX_SLOTS_PER_MESSAGE,
          title: "Max slots per message",
          description: "Maximum number of slots rendered per agent message",
          valueType: "integer",
          orientation: "horizontal",
          appearance: "standard",
          /**
           * More than a handful of times in one message reads as a wall of
           * options rather than a choice, so the cap is a product rule, not a
           * storage limit.
           */
          validation: { min: 1, max: 5 },
          presentation: { width: "compact", unit: "appointment options" },
        },
        schedulingBuffer: {
          kind: "duration",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_SCHEDULING.SCHEDULING_BUFFER,
          title: "Scheduling buffer",
          description: "How far ahead of now the earliest offered slot must be",
          orientation: "horizontal",
        },
        cancellationBuffer: {
          kind: "duration",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_SCHEDULING.CANCELLATION_BUFFER,
          title: "Cancellation buffer",
          description:
            "Minimum notice required to cancel an existing appointment",
          orientation: "horizontal",
        },
        reschedulingBuffer: {
          kind: "duration",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_SCHEDULING.RESCHEDULING_BUFFER,
          title: "Rescheduling buffer",
          description:
            "Minimum notice required to reschedule an existing appointment",
          orientation: "horizontal",
        },
      },
    },
  },
} as const satisfies ConfigSchema.Resource;

export const koraConfigPage = {
  to: ROUTES.AGENT_KORA,
  label: "Kora Behavior",
  icon: Sparkles,
  extraAnchors: [{ id: KORA_OVERVIEW_ANCHOR_ID, label: "Overview" }],
} satisfies ConfigSchema.Page;
